/// <amd-module name="Release/Landing/HubContent/Releases/ActiveReleases" />
import * as React from "react";

import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Link } from "VSSUI/Link";

import { VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { format, equals, localeFormat } from "VSS/Core/Util/String";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssFeatureService } from "VSS/Platform/Feature";

import { ZeroData, ZeroDataActionType } from "VSSUI/Components/ZeroData";

import { IReleasesFilterState } from "Release/Landing/HubContent/Filter/FilterTypes";


import { Artifact, Release, ReleaseEnvironment, ReleaseApproval, ApprovalStatus, EnvironmentStatus, DeploymentStatus, DeploymentOperationStatus } from "Release/Client/Contracts/Release";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";

import { getTFSPageData } from "Tfs/Platform/Page";

import { ActiveDefinitionReferenceType } from "Release/Landing/DataProviderContracts";
import { IReleaseDefinitionPivotCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { ReleasesViewCanvasConstants, MessageBarKeys, ActiveReleaseColumnKeys } from "Release/Landing/Constants";
import { ActiveReleasesActionCreator } from "./ActiveReleasesActionCreator";
import { ActiveReleasesTable } from "./ActiveReleasesTable";
import { ActiveReleasesActions } from "./ActiveReleasesActions";
import { IActiveReleasesState, ActiveReleasesStore, ActiveReleaseColumnResizeEvent, ActiveReleaseColumnOptionsModifiedEvent } from "./ActiveReleasesStore";
import { IReleasesSignalRHandler, getReleasesSignalRHub } from "Release/Landing/SignalR/LandingSignalRHub";
import { startScenario, PerfScenarios, endScenario } from "Release/Helpers/Performance";
import { getPrimaryArtifact, getPrimaryArtifactBranchName, getArtifactBuildInfo, getBranchDisplayValue } from "Artifact";
import { hasRmPermissions, ReleaseManagementSecurityPermissions } from "Release/Helpers/Permission";
import { ICreateReleaseCommandService } from "Release/Commands/CreateReleaseCommandService";
import { MessageActions } from "Release/Landing/HubMessageBar/MessageActions";
import * as Resources from "Release/Landing/Resources";

export interface IActiveReleasesProps extends IVssComponentProperties {
    definitionId: number;
    definitionName: string;
    definitionFolderPath: string;
    selectedFolderPath: string;
    isSearchedItem: boolean;
    definitionReferenceType: ActiveDefinitionReferenceType;
    filterState: IReleasesFilterState;
    markTTICallback: () => void;
}

interface IActiveReleasesViewState extends IActiveReleasesState {
    deletingReleaseID?: number;
    isNoResultsImageLoaded?: boolean;
}

const leftPanelDefaultWidth: number = 320;

let _onScrollRerenderTimeoutId: number = 0;

export class ActiveReleases extends VssComponent<IActiveReleasesProps, IActiveReleasesViewState>{
    constructor(props: IActiveReleasesProps, context: IVssComponentContext) {
        super(props, context);

        this._activeReleasesActionCreator = ActionCreatorManager.GetActionCreator<ActiveReleasesActionCreator>(ActiveReleasesActionCreator);
        this._activeReleasesStore = StoreManager.GetStore<ActiveReleasesStore>(ActiveReleasesStore, "active-releases-store", context.pageContext);
        this._messageActions = ActionsHubManager.GetActionsHub<MessageActions>(MessageActions);
        this._activeReleasesActions = ActionsHubManager.GetActionsHub<ActiveReleasesActions>(ActiveReleasesActions);

        // The statement below is going to make an API call the first time, or fetch data from the data provider
        this._fetchInitialReleases();

        // Add changed handler to the store after the first set of releases is fetched. This would prevent a changed handler firing a setState
        // while we are in the flow where we are fetching release. For async calls, the store will be updated in a later process queue, and the
        // view will be updated in a later setState. When data is in the data provider, the store will be updated synchronously before the
        // we add the changed handler below.
        this._activeReleasesStore.addChangedListener(this._onActiveReleasesStoreUpdate);
        this._activeReleasesStore.addListener(ActiveReleaseColumnResizeEvent, this._onColumnResize);
        this._activeReleasesStore.addListener(ActiveReleaseColumnOptionsModifiedEvent, this._onColumnOptionsModified);
        window.addEventListener("resize", this._handleBrowserResize);

        const storeState = this._activeReleasesStore.getState();
        if (storeState.definitionId === props.definitionId) {
            this.state = storeState;
        }
        else {
            this.state = {
                ...storeState,
                definitionId: props.definitionId,
                folderPath: props.folderPath,
                releases: [],
                isLoading: true,
                isNoResultsImageLoaded: false
            } as IActiveReleasesState;
        }
    }

    public componentDidMount(): void {
        this._isRDChanged = true;
        this._releasesFetched = false;

        if (this.state.isLoading === false) {
            this._endPerfScenario();

            // Fetch next batch if there is space left
            setTimeout(() => this._fetchMoreReleasesIfThereIsSpace(), 750); // Wait for 750 ms to sufficiently allow virtualized rendering to complete
        }

        this._signalRHandler = new ReleasesListSignalRHandler(this.context.pageContext, this._activeReleasesActionCreator, this._activeReleasesStore);
        const signalRHub = getReleasesSignalRHub();
        if (signalRHub) {
            signalRHub.addSignalRHanlder(this._signalRHandler);
            this._subscribeToPipeline(this.props.definitionId);
        }
    }

    public componentWillReceiveProps(nextProps: IActiveReleasesProps): void {
        if (nextProps.definitionId !== this.props.definitionId || nextProps.selectedFolderPath !== this.props.selectedFolderPath) {
            this._unsubscribeFromPipeline(this.props.definitionId);

            this._isRDChanged = true;
            this._releasesFetched = false;

            const filterState = nextProps.filterState;
            if (filterState.currentlyDeployed) {
                this._activeReleasesActionCreator.fetchActiveReleases(this.context.pageContext, nextProps.definitionId, nextProps.definitionFolderPath, nextProps.selectedFolderPath);
            } else {
                this._activeReleasesActionCreator.fetchReleases(this.context.pageContext, nextProps.definitionId, nextProps.definitionFolderPath, nextProps.selectedFolderPath);
            }

            this._batchCountOfReleasesFetched = 0;

            this.setState({
                releases: [], // Flush out releases when a new RD is chosen
                isLoading: true,
                isNoResultsImageLoaded: false
            });
        }
        else if (this._hasFilterStateChanged(nextProps.filterState)) {
            this._activeReleasesActionCreator.searchReleases(this.context.pageContext, nextProps.definitionId, nextProps.definitionFolderPath, nextProps.selectedFolderPath, nextProps.filterState);
        }
    }

    public componentDidUpdate(prevProps: IActiveReleasesProps, prevState: IActiveReleasesState): void {
        // If the list of releases for the same definition is loaded, check if there is space to load more
        if (prevProps.definitionId === this.props.definitionId &&
            prevState.isLoading
            && !this.state.isLoading) {
            setTimeout(() => this._fetchMoreReleasesIfThereIsSpace(), 750); // Wait for 750 ms to sufficiently allow virtualized rendering to complete 

            this._subscribeToPipeline(this.props.definitionId);
        }

        if (this.state.isLoading) {
            setTimeout(() => {
                if (!!this._loadMoreSpinner) {
                    this._loadMoreSpinner.scrollIntoView({
                        behavior: "smooth",
                        block: "end",
                        inline: "nearest"
                    });
                }
            }, 300); // 300ms because the spinner animation takes time to render
        }

        if (this.state.isLoading === false) {
            this.setTimeout(() => this._endPerfScenario(), 0);
        }

        this._resetPageRenderedTimestampIfRequired();
    }

    public componentWillUnmount() {
        this._activeReleasesStore.removeChangedListener(this._onActiveReleasesStoreUpdate);
        this._activeReleasesStore.removeListener(ActiveReleaseColumnOptionsModifiedEvent, this._onColumnOptionsModified);
        this._activeReleasesStore.removeListener(ActiveReleaseColumnResizeEvent, this._onColumnResize);
        window.removeEventListener("resize", this._handleBrowserResize);

        const signalRHub = getReleasesSignalRHub();
        if (signalRHub) {
            signalRHub.removeSignalRHanlder(this._signalRHandler);
        }

        this._unsubscribeFromPipeline(this.props.definitionId);
    }

    public render(): React.ReactNode {
        const envColumnWidth: number = this._activeReleasesStore.getEnvColumnWidth(this.props.definitionId);
        const releaseColumnWidth: number = this._activeReleasesStore.getReleaseColumnWidth(this.props.definitionId);

        const columnItemKey = this.props.definitionId > 0 ? this.props.definitionId : this.props.selectedFolderPath;
        const showDescriptionColumn: boolean = this._activeReleasesStore.showColumn(columnItemKey, ActiveReleaseColumnKeys.ActiveReleaseDescriptionKey);
        const showTagsColumn: boolean = this._activeReleasesStore.showColumn(columnItemKey, ActiveReleaseColumnKeys.ActiveReleaseTagsKey);
        const featureService: IVssFeatureService = this.context.pageContext.getService<IVssFeatureService>("IVssFeatureService");

        if (this.state.releases) {
            // First compute Release name column width, and then environment column width
            this._setReleaseNameColumnInitialWidth();
            this._setEnvironmentNodeWidth();
        }

        if (this.state.clearReleases) {
            return null;
        }

        if (this.state.releases && this.state.releases.length > 0) {

            let approvalsMessage = this._getApprovalsBarMessage();
            const releaseApprovalBarStyle = {
                "width": "100%"
            };
            return (
                <div className={css("releases-with-approvals-bar", this.props.cssClass)} style={releaseApprovalBarStyle}>
                    {
                        approvalsMessage &&
                        this.state.showApprovalsBar &&
                        <MessageBar
                            className={"approvals-bar"}
                            severity={MessageBarSeverity.Info}
                            onDismiss={() => this._activeReleasesActions.clearApprovalsBar.invoke({})}
                        >

                            {approvalsMessage}

                        </MessageBar>
                    }

                    {/*
                        useSignalR(this.context.pageContext)
                        && !!this.props.selectedFolderPath
                        && <MessageBar
                            className={"livness-message-bar"}
                            severity={MessageBarSeverity.Info}
                        >
                            <FormatComponent format={Resources.LivenessNotAvailableFolderMessage}>
                                <Link
                                    className={"cursor-pointer"}
                                    onClick={() => this._activeReleasesActionCreator.fetchReleases(this.context.pageContext, this.props.definitionId, this.props.definitionFolderPath, this.props.selectedFolderPath)}
                                >
                                    {Resources.ClickToRefreshLinkText}
                                </Link>
                            </FormatComponent>
                        </MessageBar>
                    */}

                    < div
                        className={"active-releases-list-holder"}
                        ref={(listHolder) => { this._releasesListHolder = listHolder; }}
                        onScroll={this._onActiveReleasesListScroll}
                    >

                        {
                            <ActiveReleasesTable
                                releases={this.state.releases}
                                definitionId={this.props.definitionId}
                                envColumnWidth={envColumnWidth}
                                releaseColumnWidth={releaseColumnWidth}
                                envNodeWidth={this._envNodeWidth}
                                initialEnvColumnWidth={this._initialEnvColumnWidth}
                                initialNameColumnWidth={this._initialNameColumnWidth}
                                initialArtifactNameWidth={this._initialArtifactNameMaxWidth}
                                initialBranchNameWidth={this._initialBranchNameMaxWidth}
                                definitionEnvironmentCurrentReleaseMap={this.state.definitionEnvironmentCurrentReleaseMap}
                                filterState={this.props.filterState}
                                onDesiredReleaseFound={this._createReleaseFoundEvent}
                                showDescriptionColumn={showDescriptionColumn}
                                showTagsColumn={showTagsColumn}
                                showDefinitionNameColumn={!!this.props.selectedFolderPath} />
                        }
                        {
                            this.state.isLoading && (
                                <div className={"active-releases-load-more"} ref={(spinner) => { this._loadMoreSpinner = spinner; }}>
                                    <Spinner
                                        className="active-releases-spinner loading-more"
                                        size={SpinnerSize.large}
                                        label={Resources.LoadingReleasesSpinnerLabel} />
                                </div>)
                        }

                    </div >
                </div >
            );
        }
        else if (this.state.isLoading) {
            return (
                <Spinner
                    className="active-releases-spinner"
                    size={SpinnerSize.large}
                    label={Resources.LoadingReleasesSpinnerLabel} />
            );
        }
        else {
            return this._getNoReleasesView();
        }
    }

    private _endPerfScenario = () => {
        // End ReleasesHub perf scenario when Releases loading is complete
        if (!this.props.isTabSwitchNavigation) {
            this.props.markTTICallback();
        }

        //End ReleasesPivot scenario when -
        // 1. there is a selection change in left panel
        // 2. or initial page load is complete
        // 3. or Right pivot change
        if (!this._releasesLoadMarked) {
            endScenario(this.context.pageContext, PerfScenarios.ReleasesPivot, false);
            this._releasesLoadMarked = true;
        }
    }

    private _fetchInitialReleases(): void {
        const filterState = this.props.filterState;
        if (filterState.currentlyDeployed) {
            this._activeReleasesActionCreator.fetchActiveReleases(this.context.pageContext, this.props.definitionId, this.props.definitionFolderPath, this.props.selectedFolderPath);
        } else {
            if (this._isFilterStateDefault()) {
                this._activeReleasesActionCreator.fetchReleases(this.context.pageContext, this.props.definitionId, this.props.definitionFolderPath, this.props.selectedFolderPath);
            }
            else {
                this._activeReleasesActionCreator.searchReleases(this.context.pageContext, this.props.definitionId, this.props.definitionFolderPath, this.props.selectedFolderPath, this.props.filterState);
            }
        }
    }

    private _getApprovalsBarMessage(): React.ReactNode {
        let environmentsWithPendingApprovals: ReleaseEnvironment[] = [];

        this.state.releases.forEach((rel: Release) => {
            rel.environments.forEach((relEnv: ReleaseEnvironment) => {
                if (this._hasPendingApprovals(relEnv) && environmentsWithPendingApprovals.map(env => env.definitionEnvironmentId).indexOf(relEnv.definitionEnvironmentId) === -1) {
                    environmentsWithPendingApprovals.push(relEnv);
                }
            });
        });

        if (environmentsWithPendingApprovals.length === 0) {
            return null;
        }

        return this._getApprovalsBarMessageFromEnvironments(environmentsWithPendingApprovals);
    }

    private _getApprovalsBarMessageFromEnvironments(environments: ReleaseEnvironment[]): React.ReactNode {

        const onApprovalNameClick = (environmentId: number) => {
            if (this._releasesListHolder) {
                const environmentNode = this._releasesListHolder.getElementsByClassName("active-rel-env-node env-" + environmentId)[0] as HTMLDivElement;
                environmentNode.scrollIntoView({
                    behavior: "smooth", inline: "start", block: "end"
                });

                setTimeout(() => {
                    // We have to use apply hover on the environment node, instead of focus
                    // TooltipHost is not working properly with focus at this moment. If we focus, and click on the approve button, nothing happens
                    const hoverEvent = document.createEvent("MouseEvent");
                    hoverEvent.initEvent("mouseover", true, false);
                    environmentNode.dispatchEvent(hoverEvent);
                }, 600);
            }
        };

        const onApprovalNameKeydown = (event: React.KeyboardEvent<HTMLElement>, environmentId: number) => {
            if (event.key === "Enter" || event.key === "" || event.key === "Spacebar") {
                onApprovalNameClick(environmentId);
                event.preventDefault();
                event.stopPropagation();
            }
        };

        const getApprovalAnchorElement = (environmentId: number, environmentName: string): JSX.Element => {
            return (<b
                /* switching to span from a for the time being, till design is closed on */
                /*role={"button"}
                tabIndex={0}
                onClick={() => { onApprovalNameClick(environmentId); }}
                onKeyDown={(e) => { onApprovalNameKeydown(e, environmentId); }}*/>
                {environmentName}
            </b>
            );
        };

        switch (environments.length) {
            case 1:
                return (
                    <FormatComponent format={Resources.PendingApprovalOneEnvironment}>
                        {getApprovalAnchorElement(environments[0].id, environments[0].name)}
                    </FormatComponent>
                );
            case 2:
                return (
                    <FormatComponent format={Resources.PendingApprovalTwoEnvironments}>
                        {getApprovalAnchorElement(environments[0].id, environments[0].name)}
                        {getApprovalAnchorElement(environments[1].id, environments[1].name)}
                    </FormatComponent>
                );
            default:
                return (
                    <FormatComponent format={Resources.PendingApprovalMultipleEnvironments}>
                        {getApprovalAnchorElement(environments[0].id, environments[0].name)}
                        {getApprovalAnchorElement(environments[1].id, environments[1].name)}
                        {(environments.length - 2).toString()}
                    </FormatComponent>
                );
        }
    }

    private _fetchMoreReleasesIfThereIsSpace(): void {
        if (this._releasesListHolder) {
            const scrollHeight = this._releasesListHolder.scrollHeight;
            const clientHeight = this._releasesListHolder.clientHeight;
            // If there is no scroll bar, ie clientHeight = scrollHeight
            // Check for rounding errors of +/- 1
            if (clientHeight <= scrollHeight + 1 && clientHeight >= scrollHeight - 1) {
                this._fetchMoreReleases();
            }
        }
    }

    private _hasPendingApprovals(relEnv: ReleaseEnvironment): boolean {
        return (relEnv.preDeployApprovals || []).some((approval: ReleaseApproval) => approval.status === ApprovalStatus.Pending)
            || (relEnv.postDeployApprovals || []).some((approval: ReleaseApproval) => approval.status === ApprovalStatus.Pending);
    }

    private _onActiveReleasesListScroll = (event: React.UIEvent<HTMLDivElement>): void => {
        clearTimeout(_onScrollRerenderTimeoutId);
        _onScrollRerenderTimeoutId = setTimeout(() => this.setState({}), 500 /* re render after 500 millisec */);

        if (!!this._releasesListHolder && this.state.continuationToken > 0) {
            let scrollPositionFromBottom = this._releasesListHolder.clientHeight + this._releasesListHolder.scrollTop - this._releasesListHolder.scrollHeight;
            if (scrollPositionFromBottom >= -20 && scrollPositionFromBottom <= 20) {
                this._fetchMoreReleases();
            }
        }
    }

    private _fetchMoreReleases(): void {
        if (!this.state.isLoading && this.state.continuationToken > 0) {
            let filterState: IReleasesFilterState = this.props.filterState;

            this._activeReleasesActionCreator.fetchMoreReleases(this.context.pageContext, this.props.definitionId > 0 ? this.props.definitionId : 0, this.props.definitionFolderPath, this.props.selectedFolderPath, filterState, this.state.continuationToken);
            this.setState(this._getLoadingState(true));
        }
    }

    private _getNoReleasesView(): React.ReactNode {
        let ariaLabel: string = Resources.NoReleasesFoundPrimaryText;
        const secondaryTextElement: JSX.Element = (
            <div className="active-rd-zero-releases-text">
                <FormatComponent format={Resources.NoReleasesFoundSecondaryText}>
                    <Link rel={"noopener noreferrer"} href={"https://docs.microsoft.com/en-us/vsts/pipelines/release/triggers?view=vsts"} target={"_blank"} className={"setup-triggers-link"}>
                        {Resources.SetupTriggersLinkText}
                    </Link>
                </FormatComponent>
            </div>
        );

        const canCreateRelease = hasRmPermissions(this.context.pageContext, this.props.definitionFolderPath, this.props.definitionId, ReleaseManagementSecurityPermissions.CreateReleases);
        if (canCreateRelease) {
            ariaLabel = localeFormat("{0} {1}", ariaLabel, localeFormat(Resources.NoReleasesFoundSecondaryText, Resources.SetupTriggersLinkText));
        }

        return (
            <div
                className={css("active-rd-no-releases-view flex flex-grow flex-column flex-center", this.props.cssClass)}
                aria-label={ariaLabel}>
                <ZeroData
                    actionText={canCreateRelease && !this.props.selectedFolderPath ? Resources.CreateReleaseText : ""} // Do not show Create release button for releases view of folders
                    actionType={ZeroDataActionType.ctaButton}
                    primaryText={Resources.NoReleasesFoundPrimaryText}
                    secondaryText={secondaryTextElement}
                    imageAltText={Resources.NoReleasesFoundPrimaryText}
                    imagePath={getAssetLocation(this.context.pageContext, "ms.vss-releaseManagement-web/release-landing/zerodata-release-management-new.png")}
                    onActionClick={this._createRelease}
                />
            </div>);
    }

    private _isFilterStateDefault(): boolean {
        if (!this.props.filterState) {
            return false;
        }

        const filterState = this.props.filterState;
        return !filterState.searchText
            && (filterState.status === 0 || (filterState.status as number) === 7)
            && !filterState.branch
            && !(filterState.tags && filterState.tags.length)
            && !filterState.createdBy
            && filterState.isDeleted === false;
    }

    private _onActiveReleasesStoreUpdate = (): void => {
        this._releasesFetched = true;
        let storeState: IActiveReleasesState = this._activeReleasesStore.getState();
        this.setState(storeState);
    }

    private _onColumnResize = (): void => {
        const currentState = this.state;
        this.setState(currentState);
    }

    private _onColumnOptionsModified = (): void => {
        const currentState = this.state;
        this.setState(currentState);
    }

    private _createReleaseFoundEvent = (): void => {
        // Not required, Remove this when we remove the prop onDesiredReleaseFound from all components
    }

    private _getLoadingState(keepReleases: boolean): IActiveReleasesViewState {
        return {
            definitionId: this.props.definitionId,
            releases: (keepReleases && this.state.releases) || [],
            isLoading: true,
            continuationToken: (keepReleases && this.state.continuationToken) || 0,
            isNoResultsImageLoaded: false
        } as IActiveReleasesViewState;
    }

    private _createRelease = (): void => {
        const commandContext = {
            pageContext: this.context.pageContext,
            pipelineId: this.props.definitionId,
            pipelineName: this.props.definitionName,
            isFolder: false,
            folderPath: this.props.definitionFolderPath,
            successCallback: (message: React.ReactNode) => {
                this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubSuccess, value: message });
            },
            errorCallback: (message: React.ReactNode) => {
                this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: message });
            }
        } as IReleaseDefinitionPivotCommandContext;

        const createReleaseCallback = (release: Release): void => {
            this._activeReleasesActionCreator.handleCreatedRelease(this.context.pageContext, release);
        };

        this.context.pageContext.getService<ICreateReleaseCommandService>("ICreateReleaseCommandService").createRelease(commandContext, createReleaseCallback);
    }

    private _hasFilterStateChanged(newFilterState: IReleasesFilterState): boolean {
        if (this.props.filterState) {
            const currentFilterState = this.props.filterState;
            const newTags: string = newFilterState.tags ? newFilterState.tags.join(",") : "";
            const currentTags: string = currentFilterState.tags ? currentFilterState.tags.join(",") : "";
            if (equals(newFilterState.branch || "", currentFilterState.branch || "") &&
                equals(newFilterState.createdBy || "", currentFilterState.createdBy || "") &&
                equals(newFilterState.searchText, currentFilterState.searchText) &&
                newFilterState.status === currentFilterState.status &&
                newFilterState.isDeleted === currentFilterState.isDeleted &&
                newFilterState.currentlyDeployed === currentFilterState.currentlyDeployed &&
                equals(newTags, currentTags)) {
                return false;
            }
        }

        return true;
    }

    private _setReleaseNameColumnInitialWidth(): void {
        // If the column is resized, and we have a column width, we use that.
        // Else we limit the release column width by a max of 800, and artifact and branch name by max of 400 each
        // 104 = 48 px for the image, 24px for margins, 32px for something (update when understood)
        const releaseNameColumnActualAvailableWidth = this._activeReleasesStore.getReleaseColumnWidth(this.props.definitionId) - 104;
        const maxAllowedWidth = Math.max(800, releaseNameColumnActualAvailableWidth);

        this._initialNameColumnWidth = maxAllowedWidth;
        this._initialArtifactNameMaxWidth = maxAllowedWidth / 2;
        this._initialBranchNameMaxWidth = maxAllowedWidth / 2;
        let releaseNameWidth = 0;
        let artifactBranchCombinedWidth = 0;

        if (this.state.releases && this.state.releases.length > 0) {
            let releasesToVerify = Math.min(this.state.releases.length, 25);
            let maxArtifactCharacters: number = 0;
            let maxBranchCharacters: number = 0;
            let maxReleaseNameCharacters: number = 0;
            for (let index = 0; index < releasesToVerify; index++) {
                let release = this.state.releases[index];

                let primaryArtifact: Artifact | undefined = getPrimaryArtifact(release.artifacts);
                let artifactName: string = getArtifactBuildInfo(primaryArtifact);
                let branchName: string = getPrimaryArtifactBranchName(release.artifacts);
                let branchDisplayName: string = getBranchDisplayValue(branchName);
                let releaseNameLength = release.name.length;

                if (maxArtifactCharacters < artifactName.length) {
                    maxArtifactCharacters = artifactName.length;
                }
                if (maxBranchCharacters < branchDisplayName.length) {
                    maxBranchCharacters = branchDisplayName.length;
                }
                if (maxReleaseNameCharacters < releaseNameLength) {
                    maxReleaseNameCharacters = releaseNameLength;
                }
            }

            // Compute release name width
            // *11 for large font size
            releaseNameWidth = (maxReleaseNameCharacters * 11) + 10 + 48; // + 20 for margins and chevron, +48 for avatar image, +36 for ellipsis button
            releaseNameWidth = Math.max(releaseNameWidth, 175); // Occupy at least 175px as otherwise shorter names will be hidden when menu item shows up on hover or focus
            this._initialArtifactNameMaxWidth = 0;
            this._initialBranchNameMaxWidth = 0;

            // Compute artifact build and branch length(if they exist)
            if (maxArtifactCharacters > 0 || maxBranchCharacters > 0) {
                // *10 for medium font size
                this._initialArtifactNameMaxWidth = maxArtifactCharacters > 0 ? (maxArtifactCharacters * 10) + 14 : 0; // +14 for artifact icon
                this._initialBranchNameMaxWidth = maxBranchCharacters > 0 ? (maxBranchCharacters * 10) + 14 : 0; // +14 for branch icon

                // For small branch and artifact names, add a greater margin to name column so that branch name is not cut by action(...) button
                // For larger branch and artifact names, the space allocated is sufficient without adding margins
                let margin: number = 0;
                if (this._initialArtifactNameMaxWidth + this._initialBranchNameMaxWidth <= 200) {
                    margin = 35;
                }

                artifactBranchCombinedWidth = this._initialArtifactNameMaxWidth + this._initialBranchNameMaxWidth + 48 + margin;
            }

            this._initialNameColumnWidth = Math.max(artifactBranchCombinedWidth, releaseNameWidth);
        }

        this._initialNameColumnWidth = Math.min(this._initialNameColumnWidth, maxAllowedWidth);
        this._initialArtifactNameMaxWidth = Math.min(this._initialArtifactNameMaxWidth, maxAllowedWidth / 2);
        this._initialBranchNameMaxWidth = Math.min(this._initialBranchNameMaxWidth, maxAllowedWidth / 2);
    }

    private _subscribeToPipeline(definitionId: number): void {
        const signalRHub = getReleasesSignalRHub()
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (signalRHub && tfsPageData && tfsPageData.project) {
            signalRHub.watchReleaseDefinitionsReleases(tfsPageData.project.id, [definitionId]);
        }
    }

    private _unsubscribeFromPipeline(definitionId: number): void {
        const signalRHub = getReleasesSignalRHub()
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (signalRHub && tfsPageData && tfsPageData.project) {
            signalRHub.stopWatchingReleaseDefinitionsReleases(tfsPageData.project.id, [definitionId]);
        }
    }

    private _resetPageRenderedTimestampIfRequired(): void {
        if (this._releasesFetched && (this._isRDChanged || this._currentlyDeployedToggleChanged)) {
            this._releasesFetched = false;
            this._isRDChanged = false;
            this._currentlyDeployedToggleChanged = false;
        }
    }

    private _setEnvironmentNodeWidth(): void {
        let envColumnWidth: number = this._activeReleasesStore.getEnvColumnWidth(this.props.definitionId);
        if (envColumnWidth <= 0) {
            this._setMaxAvailableEnvColWidth();
        }

        const availableWidth: number = envColumnWidth > 0 ? envColumnWidth - 12 : this._maxAvailableEnvColumnWidth; //12px is for table margin
        const envCountArray: number[] = this.state.releases.map((release) => release.environments.length);
        const maxEnvCount: number = Math.max(...envCountArray);
        const envCountPossibleWithLargeSize = Math.floor(availableWidth / (ReleasesViewCanvasConstants.EnvironmentNodeWidthLarge + ReleasesViewCanvasConstants.EnvironmentNodeRightMargin));
        if (envCountPossibleWithLargeSize >= maxEnvCount) {
            this._envNodeWidth = ReleasesViewCanvasConstants.EnvironmentNodeWidthLarge;
            this._initialEnvColumnWidth = (ReleasesViewCanvasConstants.EnvironmentNodeWidthLarge + ReleasesViewCanvasConstants.EnvironmentNodeRightMargin) * maxEnvCount;
        }
        else {
            const environmentMinPosibleSize = (availableWidth / maxEnvCount) - ReleasesViewCanvasConstants.EnvironmentNodeRightMargin;
            if (environmentMinPosibleSize <= ReleasesViewCanvasConstants.EnvironmentNodeWidthSmall) {
                this._envNodeWidth = ReleasesViewCanvasConstants.EnvironmentNodeWidthSmall;
                const envCountPossibleWithSmallSize = Math.floor(availableWidth / (ReleasesViewCanvasConstants.EnvironmentNodeWidthSmall + ReleasesViewCanvasConstants.EnvironmentNodeRightMargin));
                if (envCountPossibleWithSmallSize >= maxEnvCount) {
                    this._initialEnvColumnWidth = (ReleasesViewCanvasConstants.EnvironmentNodeWidthSmall + ReleasesViewCanvasConstants.EnvironmentNodeRightMargin) * maxEnvCount;
                }
                else {
                    const showMoreButtonWidth = 20;
                    this._initialEnvColumnWidth = availableWidth - showMoreButtonWidth;
                }
            }
            else {
                this._envNodeWidth = environmentMinPosibleSize;
                this._initialEnvColumnWidth = (environmentMinPosibleSize + ReleasesViewCanvasConstants.EnvironmentNodeRightMargin) * maxEnvCount;
            }
        }
    }

    private _setMaxAvailableEnvColWidth(): void {
        // clientWidth and other calculations is causing the DOM to run a layout cycle causing slowness. Let us try to avoid that
        // So let us assume some of these widths as the original widths (i,e before resizes)
        const navigationPanelWidth = 48; // Take the vallue of the collapsed one. We would not try to squeeze ourselves if this is expanded
        const createdColumnHeaderWidth = 150 + 16; // createdColWidth + header margin
        const releaseNameHeaderWidth = this._initialNameColumnWidth + 16;
        this._maxAvailableEnvColumnWidth = window.innerWidth - leftPanelDefaultWidth - releaseNameHeaderWidth - createdColumnHeaderWidth - navigationPanelWidth;
    }


    private _handleBrowserResize = (): void => {
        this.setState(prevState => prevState);
    }

    private _activeReleasesActionCreator: ActiveReleasesActionCreator;
    private _activeReleasesActions: ActiveReleasesActions;
    private _activeReleasesStore: ActiveReleasesStore;
    private _messageActions: MessageActions;
    private _releasesListHolder: HTMLDivElement | null;
    private _loadMoreSpinner: HTMLDivElement | null;
    private _batchCountOfReleasesFetched: number = 0;
    private _initialEnvColumnWidth: number;
    private _initialNameColumnWidth: number;
    private _initialArtifactNameMaxWidth: number;
    private _initialBranchNameMaxWidth: number;
    private _maxAvailableEnvColumnWidth: number;

    // variables to handle "Release_Found" event for telemetry
    private _isRDChanged: boolean = false;
    private _currentlyDeployedToggleChanged: boolean = false;
    private _releasesFetched: boolean = false;
    private _envNodeWidth: number;
    private _releasesLoadMarked: boolean = false;
    private _signalRHandler: ReleasesListSignalRHandler;
}

class ReleasesListSignalRHandler implements IReleasesSignalRHandler {
    constructor(private _pageContext: IVssPageContext, private _actionCreator: ActiveReleasesActionCreator, private _store: ActiveReleasesStore) { }

    public onReleaseEnvironmentStatus(definitionId: number, releaseId: number, environmentId: number, environmentStatus: EnvironmentStatus, deploymentStatus: DeploymentStatus, operationStatus: DeploymentOperationStatus): void {
        const state = this._store.getState();
        if (state.definitionId > 0 && state.definitionId === definitionId) {
            this._actionCreator.updateReleaseEnvironmentStatus(definitionId, releaseId, environmentId, environmentStatus, deploymentStatus, operationStatus);
        }
    }

    public onReleaseCreated(definitionId: number, releaseId: number): void {
        const state = this._store.getState();
        if (state.filterState) {
            if (state.filterState.currentlyDeployed || state.filterState.isDeleted) {
                return;
            }
        }

        if (state.definitionId > 0 && state.definitionId === definitionId) {
            this._actionCreator.getCreatedRelease(this._pageContext, releaseId);
        }
    }

    public onDeployJobStartedEvent(definitionId: number, releaseId: number, definitionEnvironmentId: number, releaseEnvironmentId: number): void {
        this._fetchAndUpdateEnvironment(definitionId, releaseId, releaseEnvironmentId, true);
    }

    public onReleaseApprovalPending(definitionId: number, releaseId: number, releaseEnvironmentId: number, approvalId: number, approverId: string): void {
        this._fetchAndUpdateEnvironment(definitionId, releaseId, releaseEnvironmentId, false);
    }

    private _fetchAndUpdateEnvironment(definitionId: number, releaseId: number, releaseEnvironmentId: number, updateOnlyNotDeployed: boolean): void {
        const state = this._store.getState();
        if (state.definitionId > 0 && state.definitionId === definitionId) {
            const release = state.releases.find(rel => rel.id === releaseId);
            if (release) {
                const environment = release.environments.find(env => env.id === releaseEnvironmentId);
                if (environment && !(updateOnlyNotDeployed && environment.deploySteps.length > 0)) {
                    this._actionCreator.fetchAndUpdateEnvironment(this._pageContext, definitionId, releaseId, releaseEnvironmentId, updateOnlyNotDeployed);
                }
            }
        }
    }
}